import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowDownRight, MessageCircle, ShieldCheck, TrendingUp, Flame } from "lucide-react";
import { scrollToSection } from "./Shared";
import { WHATSAPP_URL } from "@/lib/site";

const HEADLINE = ["Master Bursa, SGX &", "Global Markets with", "Institutional Alpha"];

function ParticleField() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h;
    const resize = () => {
      w = canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      h = canvas.height = canvas.offsetHeight * window.devicePixelRatio;
    };
    resize();
    window.addEventListener("resize", resize);

    const dots = Array.from({ length: 70 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.6 + 0.4,
      vx: (Math.random() - 0.5) * 0.0004,
      vy: (Math.random() - 0.5) * 0.0004,
      gold: Math.random() > 0.75,
    }));

    const tick = () => {
      ctx.clearRect(0, 0, w, h);
      for (const d of dots) {
        d.x = (d.x + d.vx + 1) % 1;
        d.y = (d.y + d.vy + 1) % 1;
        ctx.beginPath();
        ctx.arc(d.x * w, d.y * h, d.r * window.devicePixelRatio, 0, Math.PI * 2);
        ctx.fillStyle = d.gold ? "rgba(212,175,55,0.5)" : "rgba(0,230,118,0.4)";
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 h-full w-full" aria-hidden="true" />;
}

const MOVERS = [
  { sym: "GAMUDA.KL", name: "Gamuda Bhd", chg: "+6.42%", price: "8.940", bars: [30, 45, 40, 62, 78, 96] },
  { sym: "GENM.KL", name: "Genting Malaysia", chg: "+5.87%", price: "2.415", bars: [26, 40, 52, 48, 70, 90] },
  { sym: "YTL.KL", name: "YTL Corp", chg: "+5.31%", price: "3.760", bars: [22, 36, 44, 58, 66, 84] },
  { sym: "SIME.KL", name: "Sime Darby Bhd", chg: "+5.08%", price: "2.690", bars: [20, 34, 30, 50, 60, 78] },
];

function MoversCard() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((v) => (v + 1) % MOVERS.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      data-testid="hero-signal-card"
      className="glow-alpha w-full max-w-md rounded-2xl border border-line bg-surface/90 p-6 backdrop-blur-xl"
    >
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Live Screen &middot; Bursa Daily</p>
        <span className="flex items-center gap-1.5 rounded-full bg-alpha/10 px-2.5 py-1 font-mono text-[10px] font-semibold text-alpha">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-alpha" /> LIVE
        </span>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Flame size={15} className="text-gold" />
        <p className="font-display text-sm font-bold text-slate-100">Today&rsquo;s &gt;5% Movers</p>
      </div>

      <div className="mt-4 space-y-2">
        {MOVERS.map((m, idx) => (
          <motion.div
            key={m.sym}
            data-testid={`mover-row-${idx}`}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1 + idx * 0.15, duration: 0.5 }}
            className={`flex items-center justify-between rounded-lg border px-3.5 py-2.5 transition-colors duration-500 ${
              active === idx ? "border-alpha/50 bg-alpha/10" : "border-line bg-panel"
            }`}
          >
            <div className="min-w-0">
              <p className="truncate font-mono text-xs font-bold text-slate-100">{m.sym}</p>
              <p className="truncate text-[10px] text-slate-500">{m.name}</p>
            </div>
            <div className="flex h-7 items-end gap-0.5" aria-hidden="true">
              {m.bars.map((b, i) => (
                <motion.span
                  key={i}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ delay: 1.2 + idx * 0.15 + i * 0.05, duration: 0.4 }}
                  style={{ height: `${b}%` }}
                  className={`w-1 origin-bottom rounded-full ${active === idx ? "bg-alpha" : "bg-slate-600"}`}
                />
              ))}
            </div>
            <div className="w-16 text-right">
              <AnimatePresence mode="wait">
                <motion.p
                  key={active === idx ? "on" : "off"}
                  initial={{ y: 6, opacity: 0.4 }}
                  animate={{ y: 0, opacity: 1 }}
                  className="font-mono text-sm font-bold text-alpha"
                >
                  {m.chg}
                </motion.p>
              </AnimatePresence>
              <p className="font-mono text-[10px] text-slate-500">{m.price}</p>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-line pt-3.5 font-mono text-[10px] text-slate-600">
        <span>Sjenie daily screen &middot; Bursa gainers &gt; 5%</span>
        <span className="uppercase tracking-[0.2em]">Demo feed</span>
      </div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28">
      <ParticleField />
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-alpha/10 blur-[140px]"
        aria-hidden="true"
      />
      <div className="pointer-events-none absolute top-40 -right-32 h-96 w-96 rounded-full bg-gold/10 blur-[120px]" aria-hidden="true" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 rounded-full border border-line bg-surface/70 px-4 py-1.5 backdrop-blur"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-alpha" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
              Kuala Lumpur &middot; Singapore &middot; Est. Desk 2008
            </span>
          </motion.div>

          <h1 className="mt-7 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-slate-100 sm:text-5xl lg:text-6xl">
            {HEADLINE.map((line, idx) => (
              <span key={idx} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${idx === 2 ? "text-gradient-alpha" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + idx * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="mt-6 max-w-xl text-sm leading-relaxed text-slate-400 md:text-base"
          >
            Precision trading signals engineered for Malaysian and Singaporean capital — automated Syariah
            screening, HSI momentum and IPO access, with target profits from RM300 up to RM10,000 per trade.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-plans"
              onClick={() => scrollToSection("#pricing")}
              className="group flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-obsidian transition-transform duration-300 hover:scale-[1.04]"
            >
              View Membership Plans
              <ArrowDownRight size={17} className="transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <a
              data-testid="hero-cta-whatsapp"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full border border-alpha/50 px-7 py-3.5 text-sm font-semibold text-alpha transition-colors duration-300 hover:bg-alpha/10"
            >
              <MessageCircle size={17} /> WhatsApp the Desk
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-6 font-mono text-xs text-slate-500"
          >
            <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-alpha" /> Syariah-screened setups</span>
            <span className="flex items-center gap-2"><TrendingUp size={14} className="text-gold" /> 86.4% win rate YTD</span>
          </motion.div>
        </div>

        <motion.div
          style={{ y: cardY }}
          initial={{ opacity: 0, y: 60, rotate: 2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ delay: 0.6, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center lg:justify-end"
        >
          <MoversCard />
        </motion.div>
      </div>
    </section>
  );
}
